import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import RdcPanel from '@/components/rdc/RdcPanel'
import { useOpportunity, useOpportunityActivities, useCreateActivity, useUpdateOpportunity, useMoveOpportunityStage, useDeleteOpportunity, useUpdateActivity, useDeleteActivity, useOpportunityAttachments, useUploadOpportunityAttachment, useDeleteOpportunityAttachment, type OpportunityAttachment } from '@/hooks/useOpportunities'
import { useProposals, useDeleteProposal, fetchProposalPreviewBlob, type Proposal } from '@/hooks/useProposals'
import { useCompany, useCompanyContacts, useLead, useUpdateLead, useDeleteContact } from '@/hooks/useLeads'
import { TasksPanel } from '@/components/TasksPanel'
import { SnovEnrichButton, SnovSuggestButton } from '@/components/SnovButtons'
import { QualifyBantModal } from '@/components/QualifyBantModal'
import { ContactFormModal } from '@/components/ContactFormModal'
import { CreateProposalWizard } from '@/components/CreateProposalWizard'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import CompactRichTextEditor from '@/components/ui/CompactRichTextEditor'
import { sanitizeHtml, looksLikeHtml } from '@/lib/sanitize'
import type { Contact, OpportunityStage } from '@/types'
import { useAuthStore } from '@/stores/authStore'
import { STAGE_CONFIG, STAGE_ORDER } from '@/stores/kanbanStore'
import { Button } from '@/components/ui/Button'
import { CopyButton } from '@/components/ui/CopyButton'
import { NextBestActionModal } from '@/components/NextBestActionModal'
import { Badge } from '@/components/ui/Badge'
import { UserAvatar } from '@/components/ui/Avatar'
import { formatCurrency, formatDate, formatDateTime, formatRelativeTime } from '@/lib/utils'
import type { ActivityChannel, OpportunityActivity } from '@/types'
import { CHANNEL_CONFIG } from '@/types'
import {
  ArrowLeft, Building2, User, Calendar, DollarSign, TrendingUp, FileText,
  Loader2, Send, Mail, MessageCircle, Phone, Users, StickyNote,
  CheckCircle2, XCircle, Clock, Pencil, Plus, ExternalLink, X, Target, Star, ChevronDown, Eye, EyeOff, ClipboardCheck,
  Trash2, Save, Paperclip, Download, Upload, Sparkles} from 'lucide-react'

const CHANNEL_ICONS: Record<ActivityChannel, React.ReactNode> = {
  email: <Mail className="h-3.5 w-3.5" />,
  whatsapp: <MessageCircle className="h-3.5 w-3.5" />,
  phone: <Phone className="h-3.5 w-3.5" />,
  meeting: <Users className="h-3.5 w-3.5" />,
  note: <StickyNote className="h-3.5 w-3.5" />,
}

const ORIGIN_LABELS: Record<string, string> = {
  event: 'Evento',
  bdr_partners: 'BDR/Parceiro/Indicação',
  active_prospecting: 'Prospecção Ativa',
  website: 'Website',
}

function isActivityContentEmpty(html: string): boolean {
  if (!html) return true
  return !html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
}

function groupByDate(activities: OpportunityActivity[]): { label: string; items: OpportunityActivity[] }[] {
  const groups: Record<string, OpportunityActivity[]> = {}
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  for (const act of activities) {
    const d = new Date(act.created_at)
    let label: string
    if (d.toDateString() === today.toDateString()) {
      label = 'Hoje'
    } else if (d.toDateString() === yesterday.toDateString()) {
      label = 'Ontem'
    } else {
      label = formatDate(act.created_at)
    }
    if (!groups[label]) groups[label] = []
    groups[label].push(act)
  }

  return Object.entries(groups).map(([label, items]) => ({ label, items }))
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

export function OpportunityDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const updateOppMut = useUpdateOpportunity()
  const moveStageMut = useMoveOpportunityStage()
  const deleteOppMut = useDeleteOpportunity()
  const updateActivityMut = useUpdateActivity()
  const deleteActivityMut = useDeleteActivity()
  const [editingActivityId, setEditingActivityId] = useState<string | null>(null)
  const [editingActivityContent, setEditingActivityContent] = useState('')
  const [showEditOpp, setShowEditOpp] = useState(false)
  const [showCreateProposal, setShowCreateProposal] = useState(false)
  const [showNBA, setShowNBA] = useState(false)
  const [proposalsExpanded, setProposalsExpanded] = useState(false)
  const [editProposal, setEditProposal] = useState<Proposal | null>(null)
  const [deleteProposal, setDeleteProposal] = useState<Proposal | null>(null)
  const [previewLoading, setPreviewLoading] = useState<string | null>(null)
  const deleteProposalMut = useDeleteProposal()
  const [editForm, setEditForm] = useState({ name: '', value: 0, probability: 50, expected_close_date: '', stage: 'qualified' as OpportunityStage })
  const [editError, setEditError] = useState<string | null>(null)
  const [confirmDeleteOpp, setConfirmDeleteOpp] = useState(false)
  const [deletingOpp, setDeletingOpp] = useState(false)
  const { data: opp, isLoading } = useOpportunity(id || null)
  const proposals = useProposals({ opportunityId: id || null, perPage: 50 })
  const { data: activities, isLoading: loadingActivities } = useOpportunityActivities(id || null)
  const createActivity = useCreateActivity()
  const currentUser = useAuthStore((s) => s.user)

  // Dados do lead/empresa associados (carregam após o opp chegar)
  const { data: linkedLead } = useLead(opp?.lead_id || null)
  const { data: linkedCompany } = useCompany(opp?.lead_company_id || null)
  const { data: linkedContacts, refetch: refetchContacts } = useCompanyContacts(opp?.lead_company_id || null)

  // Seleção de contatos relevantes desta oportunidade (localStorage por opp).
  // Default: todos selecionados. Desmarcar = não exibe nessa view.
  const SELECTED_KEY = `opp:${id || 'x'}:selected-contacts`
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string> | null>(() => {
    try {
      const raw = localStorage.getItem(SELECTED_KEY)
      return raw ? new Set(JSON.parse(raw)) : null  // null = "todos"
    } catch { return null }
  })
  const isContactSelected = (cid: string) => selectedContactIds === null || selectedContactIds.has(cid)
  const toggleContactSelected = (contactId: string) => {
    setSelectedContactIds(prev => {
      // Se nunca foi mexido (todos), inicializa com todos os IDs
      const base: Set<string> = prev === null
        ? new Set((linkedContacts || []).map(c => c.id))
        : new Set(prev)
      if (base.has(contactId)) base.delete(contactId); else base.add(contactId)
      try { localStorage.setItem(SELECTED_KEY, JSON.stringify([...base])) } catch {}
      return base
    })
  }

  // Edit contact modal
  const [editingContact, setEditingContact] = useState<Contact | null>(null)
  const [showNewContactForm, setShowNewContactForm] = useState(false)
  // Modo gerenciar — quando true, mostra TODOS os contatos com checkboxes pra (re)selecionar
  const [manageMode, setManageMode] = useState(false)
  const deleteContactMut = useDeleteContact()
  // Modal canônico de qualificação BANT (mesmo da listagem de leads)
  const [showQualifyModal, setShowQualifyModal] = useState(false)

  const [content, setContent] = useState('')
  const [channel, setChannel] = useState<ActivityChannel>('note')

  if (isLoading) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin" /></div>
  }

  if (!opp) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Oportunidade nao encontrada</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/pipeline')}>
          <ArrowLeft className="h-4 w-4 mr-2" />Voltar ao Pipeline
        </Button>
      </div>
    )
  }

  const stageInfo = STAGE_CONFIG[opp.stage] || { title: opp.stage, color: '#888' }

  const openEditOpp = () => {
    setEditForm({
      name: opp.name || '',
      value: opp.value || 0,
      probability: opp.probability ?? 50,
      expected_close_date: opp.expected_close_date ? opp.expected_close_date.slice(0, 10) : '',
      stage: opp.stage,
    })
    setEditError(null)
    setShowEditOpp(true)
  }

  const saveEditOpp = async () => {
    setEditError(null)
    try {
      await updateOppMut.mutateAsync({
        oppId: opp.id,
        data: {
          name: editForm.name.trim(),
          value: Number(editForm.value),
          probability: Number(editForm.probability),
          expected_close_date: editForm.expected_close_date || null,
        },
      })
      // Mudança de status usa o endpoint /stage, que ajusta won_at/lost_at,
      // probabilidade e o status do lead vinculado.
      if (editForm.stage !== opp.stage) {
        await moveStageMut.mutateAsync({ oppId: opp.id, stage: editForm.stage })
      }
      setShowEditOpp(false)
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || (e as Error).message || 'Erro ao salvar'
      setEditError(msg)
    }
  }

  const handleDeleteOpp = async () => {
    setDeletingOpp(true)
    try {
      // Backend faz exclusão em 2 passos: 1ª chamada num registro não-perdido faz
      // soft delete (marca 'lost'); a 2ª, num registro já 'lost', remove em definitivo
      // com cascade (propostas, atividades, tarefas). Encadeamos as duas.
      await deleteOppMut.mutateAsync(opp.id)
      if (opp.stage !== 'lost') {
        await deleteOppMut.mutateAsync(opp.id)
      }
      navigate('/pipeline')
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || (e as Error).message || 'Erro ao excluir'
      setDeletingOpp(false)
      setConfirmDeleteOpp(false)
      alert(msg)
    }
  }

  const handleSubmit = async () => {
    if (isActivityContentEmpty(content) || !id) return
    try {
      await createActivity.mutateAsync({ oppId: id, channel, content: content.trim() })
      setContent('')
    } catch {
      // error handling — TanStack Query will show error state
    }
  }

  const grouped = groupByDate(activities || [])

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)]">
      {/* ===== HEADER ===== */}
      <div className="border-b bg-card px-6 py-4">
        <div className="flex items-center gap-3 mb-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" />Voltar
          </Button>
          <div className="h-4 w-px bg-border" />
          <h1 className="text-xl font-bold truncate">{opp.name}</h1>
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold text-white"
            style={{ backgroundColor: stageInfo.color }}
          >
            {stageInfo.title}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={openEditOpp} title="Editar Oportunidade">
              <Pencil className="h-4 w-4 mr-1.5" /> Editar Oportunidade
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowNBA(true)} title="Sugerir próxima ação com IA"><Sparkles className="h-4 w-4 mr-1.5" />Sugerir próxima ação</Button>
            <Button size="sm" onClick={() => setShowCreateProposal(true)} title="Criar Proposta vinculada a esta oportunidade">
              <FileText className="h-4 w-4 mr-1.5" /> Criar Proposta
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          {opp.lead_title && (
            <span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" />Lead: {opp.lead_title}</span>
          )}
          {opp.lead_company_name && (
            <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{opp.lead_company_name}</span>
          )}
          {opp.contact_name && (
            <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{opp.contact_name}</span>
          )}
          {opp.owner_name && (
            <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />Resp: {opp.owner_name}</span>
          )}
          {opp.lead_origin && (
            <span className="flex items-center gap-1"><Target className="h-3.5 w-3.5" />{ORIGIN_LABELS[opp.lead_origin] || opp.lead_origin}</span>
          )}
          {opp.lead_sub_origin && (
            <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5" />{opp.lead_sub_origin}</span>
          )}
        </div>
      </div>

      {/* ===== SUMMARY CARDS ===== */}
      <div className="px-6 py-4 border-b bg-muted/30">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div className="flex items-center gap-3 rounded-lg border bg-card p-3">
            <div className="rounded-full bg-primary/10 p-2"><DollarSign className="h-4 w-4 text-primary" /></div>
            <div><p className="text-xs text-muted-foreground">Valor</p><p className="font-bold text-sm">{formatCurrency(opp.value)}</p></div>
          </div>
          <div className="flex items-center gap-3 rounded-lg border bg-card p-3">
            <div className="rounded-full bg-primary/10 p-2"><TrendingUp className="h-4 w-4 text-primary" /></div>
            <div><p className="text-xs text-muted-foreground">Probabilidade</p><p className="font-bold text-sm">{opp.probability}%</p></div>
          </div>
          <div className="flex items-center gap-3 rounded-lg border bg-card p-3">
            <div className="rounded-full bg-primary/10 p-2"><Calendar className="h-4 w-4 text-primary" /></div>
            <div><p className="text-xs text-muted-foreground">Previsao</p><p className="font-bold text-sm">{opp.expected_close_date ? formatDate(opp.expected_close_date) : '-'}</p></div>
          </div>
          <button
            type="button"
            onClick={() => setProposalsExpanded(v => !v)}
            className={`flex items-center gap-3 rounded-lg border bg-card p-3 hover:bg-accent/30 transition-colors text-left ${proposalsExpanded ? 'ring-2 ring-primary/40' : ''}`}
            title="Clique para ver as propostas vinculadas"
          >
            <div className="rounded-full bg-primary/10 p-2"><FileText className="h-4 w-4 text-primary" /></div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Propostas</p>
              <p className="font-bold text-sm">{opp.proposals_count}</p>
            </div>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${proposalsExpanded ? 'rotate-180' : ''}`} />
          </button>
          {/* BANT — card próprio, clicável pra abrir qualificação completa */}
          <button
            type="button"
            onClick={() => setShowQualifyModal(true)}
            className="flex items-center gap-3 rounded-lg border bg-card p-3 hover:bg-accent/30 transition-colors text-left"
            title="Abrir qualificação BANT completa"
            disabled={!linkedLead}
          >
            <div className="rounded-full bg-primary/10 p-2"><ClipboardCheck className="h-4 w-4 text-primary" /></div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">BANT</p>
              <div className="flex items-center gap-1 mt-0.5">
                {linkedLead ? (['budget_confirmed','authority_confirmed','need_confirmed','timeline_confirmed'] as const).map((f, i) => {
                  const letter = ['B','A','N','T'][i]
                  const checked = linkedLead[f]
                  return (
                    <span key={letter} className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold ${checked ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'}`}>{letter}</span>
                  )
                }) : <span className="text-xs text-muted-foreground">—</span>}
              </div>
            </div>
            <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* ===== PROPOSTAS (lista expansivel ao clicar no card) ===== */}
      {proposalsExpanded && (
        <div className="px-6 py-3 border-b">
          <div className="rounded-lg border bg-card">
            <div className="flex items-center justify-between px-4 py-2.5 border-b">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">Propostas vinculadas</span>
                <span className="text-xs text-muted-foreground">({proposals.data?.items?.length || 0})</span>
              </div>
              <Button size="sm" variant="outline" onClick={() => setShowCreateProposal(true)}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Nova proposta
              </Button>
            </div>
            {proposals.isLoading ? (
              <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Carregando propostas…</div>
            ) : (proposals.data?.items?.length || 0) === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Nenhuma proposta criada ainda.</div>
            ) : (
              <div className="divide-y">
                {proposals.data!.items.map(p => {
                  const annual = (p.one_time_total || 0) + (p.recurring_monthly_total || 0) * 12
                  const statusColor = p.status === 'sent' || p.status === 'accepted'
                    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
                    : p.status === 'rejected' || p.status === 'declined'
                    ? 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30'
                    : p.status === 'expired'
                    ? 'bg-zinc-500/15 text-zinc-600 dark:text-zinc-400 border-zinc-500/30'
                    : 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30'
                  return (
                    <div key={p.id} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm">v{p.version}</span>
                          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${statusColor}`}>{p.status}</span>
                          <span className="text-sm font-medium">{formatCurrency(annual)}</span>
                          <span className="text-xs text-muted-foreground">/ ano</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
                          <span>Criada em {formatDate(p.created_at)}</span>
                          <span>Validade {formatDate(p.valid_until)}</span>
                          {p.payment_term && <span>· {p.payment_term}</span>}
                          {p.contract_period && <span>· {p.contract_period}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" title="Visualizar proposta"
                          disabled={previewLoading === p.id}
                          onClick={async () => {
                            setPreviewLoading(p.id)
                            try {
                              const url = await fetchProposalPreviewBlob(p.id)
                              window.open(url, '_blank', 'noopener,noreferrer')
                              // Revoke depois de 1 min — tempo o suficiente pra abrir e renderizar
                              setTimeout(() => URL.revokeObjectURL(url), 60_000)
                            } catch (e: any) {
                              alert('Erro ao abrir preview: ' + (e?.response?.data?.detail || e?.message || ''))
                            } finally {
                              setPreviewLoading(null)
                            }
                          }}>
                          {previewLoading === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button size="sm" variant="ghost" title="Editar proposta"
                          onClick={() => setEditProposal(p)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" title="Excluir proposta"
                          onClick={() => setDeleteProposal(p)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== EMPRESA & CONTATOS (colapsáveis, fechados por padrão) ===== */}
      <div className="px-6 py-3 border-b space-y-2">
        {/* Empresa — colapsável */}
        <details className="group rounded-lg border bg-card">
          <summary className="cursor-pointer list-none flex items-center justify-between px-4 py-2.5 select-none hover:bg-accent/30 transition-colors rounded-lg">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
              <span className="text-sm font-semibold">Empresa</span>
              {linkedCompany && (
                <span className="text-xs text-muted-foreground truncate">— {linkedCompany.name}{linkedCompany.cnpj ? ` · ${linkedCompany.cnpj}` : ''}</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" />
            </div>
          </summary>
          <div className="px-4 pb-4 pt-1 border-t">
            {linkedCompany ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm mt-3">
                <div><span className="text-muted-foreground text-xs">Razão social</span><div className="font-medium">{linkedCompany.legal_name || linkedCompany.name}</div></div>
                {linkedCompany.cnpj && <div><span className="text-muted-foreground text-xs">CNPJ</span><div className="font-mono">{linkedCompany.cnpj}</div></div>}
                {linkedCompany.industry && <div><span className="text-muted-foreground text-xs">Setor</span><div>{linkedCompany.industry}</div></div>}
                {linkedCompany.website && <div><span className="text-muted-foreground text-xs">Website</span><div><a href={linkedCompany.website.startsWith('http') ? linkedCompany.website : `https://${linkedCompany.website}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate inline-block max-w-full">{linkedCompany.website}</a></div></div>}
                {(linkedCompany.city || linkedCompany.state) && <div><span className="text-muted-foreground text-xs">Localização</span><div>{[linkedCompany.city, linkedCompany.state].filter(Boolean).join(' - ')}</div></div>}
                {linkedCompany.phone && <div><span className="text-muted-foreground text-xs">Telefone</span><div>{linkedCompany.phone}</div></div>}
                {linkedCompany.email && <div className="sm:col-span-2"><span className="text-muted-foreground text-xs">Email corporativo</span><div>{linkedCompany.email}</div></div>}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mt-3">Carregando...</p>
            )}

            {linkedLead?.notes && (
              <div className="mt-4 pt-3 border-t">
                <span className="text-muted-foreground text-xs">Notas do Lead</span>
                <p className="text-xs whitespace-pre-wrap mt-0.5">{linkedLead.notes}</p>
              </div>
            )}
          </div>
        </details>

        {/* Contatos — colapsável (fechado por padrao, igual Empresa) */}
        <details className="group rounded-lg border bg-card">
          <summary className="cursor-pointer list-none flex items-center justify-between px-4 py-2.5 select-none hover:bg-accent/30 transition-colors rounded-lg">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Users className="h-4 w-4 text-primary flex-shrink-0" />
              <span className="text-sm font-semibold">Contatos</span>
              {linkedContacts && (
                <span className="text-xs text-muted-foreground">
                  — {linkedContacts.filter(c => isContactSelected(c.id)).length} de {linkedContacts.length} marcados
                </span>
              )}
            </div>
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180 flex-shrink-0" />
          </summary>
          <div className="px-4 pb-4 pt-1 border-t">
            {linkedContacts && linkedContacts.length > 0 ? (() => {
              const selectedContacts = linkedContacts.filter(c => isContactSelected(c.id))
              const hiddenContacts = linkedContacts.filter(c => !isContactSelected(c.id))
              const visibleList = manageMode ? linkedContacts : selectedContacts
              return (
                <>
                  <div className="flex items-center justify-between mt-3 mb-2 gap-2 flex-wrap">
                    <p className="text-xs text-muted-foreground">
                      {manageMode
                        ? `Marque ☑ os contatos que devem aparecer. Clique em "Concluir" pra voltar à visão filtrada.`
                        : `Mostrando ${selectedContacts.length} contato(s) selecionado(s).`}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant={manageMode ? 'default' : 'outline'}
                        onClick={() => setManageMode(v => !v)}
                      >
                        {manageMode ? 'Concluir' : `Gerenciar visibilidade${hiddenContacts.length > 0 ? ` (${hiddenContacts.length} oculto${hiddenContacts.length > 1 ? 's' : ''})` : ''}`}
                      </Button>
                      {opp.lead_company_id && (
                        <>
                          <SnovSuggestButton companyId={opp.lead_company_id} onCreated={() => refetchContacts()} />
                          <SnovEnrichButton companyId={opp.lead_company_id} onEnriched={() => refetchContacts()} />
                          <Button size="sm" variant="outline" onClick={() => setShowNewContactForm(true)}>
                            <Plus className="h-3.5 w-3.5 mr-1" /> Novo
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {visibleList.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                      Nenhum contato selecionado. Clique em "Gerenciar visibilidade" para escolher quais aparecem.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                      {visibleList.map(c => {
                        const selected = isContactSelected(c.id)
                        return (
                          <div key={c.id} className={`border rounded-lg p-2.5 text-sm transition-opacity ${manageMode && !selected ? 'opacity-50' : ''}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                {manageMode && (
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={() => toggleContactSelected(c.id)}
                                    className="h-4 w-4 flex-shrink-0"
                                    title={selected ? 'Desmarcar (oculta nesta oportunidade)' : 'Marcar como relevante para esta oportunidade'}
                                  />
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium truncate">
                                    {c.is_primary && <span className="text-amber-500 mr-1">★</span>}
                                    {c.display_name || c.first_name}
                                  </div>
                                  {c.job_title && <div className="text-xs text-muted-foreground truncate">{c.job_title}</div>}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                  onClick={() => setEditingContact(c)}
                                  className="p-1 rounded hover:bg-accent"
                                  title="Editar contato"
                                >
                                  <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!confirm(`Excluir o contato "${c.display_name || c.first_name}"?`)) return
                                    try {
                                      await deleteContactMut.mutateAsync(c.id)
                                      refetchContacts()
                                    } catch (e: any) {
                                      alert(e?.response?.data?.detail || 'Erro ao excluir contato')
                                    }
                                  }}
                                  className="p-1 rounded hover:bg-destructive/10 text-destructive"
                                  title="Excluir contato"
                                  disabled={deleteContactMut.isPending}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                            <div className={`mt-1 space-y-0.5 text-xs text-muted-foreground ${manageMode ? 'pl-6' : ''}`}>
                              {c.email && (
                                <div className="flex items-center gap-1">
                                  <span className="font-mono truncate" title={c.email}>{c.email}</span>
                                  <CopyButton value={c.email} label="email" size="xs" />
                                </div>
                              )}
                              {(c.phone || c.mobile) && (
                                <div className="flex items-center gap-1">
                                  <span className="font-mono">{c.phone || c.mobile}</span>
                                  <CopyButton value={c.phone || c.mobile} label="telefone" size="xs" />
                                </div>
                              )}
                              {c.whatsapp && (
                                <div className="flex items-center gap-1">
                                  <span className="font-mono">{c.whatsapp}</span>
                                  <CopyButton value={c.whatsapp} label="whatsapp" size="xs" />
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </>
              )
            })() : (
              <div className="mt-3 space-y-3">
                <p className="text-sm text-muted-foreground">Nenhum contato cadastrado.</p>
                {opp.lead_company_id && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <SnovSuggestButton companyId={opp.lead_company_id} onCreated={() => refetchContacts()} />
                    <SnovEnrichButton companyId={opp.lead_company_id} onEnriched={() => refetchContacts()} />
                    <Button size="sm" variant="outline" onClick={() => setShowNewContactForm(true)}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Novo
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </details>

        {/* Modais de contato */}
        {editingContact && opp.lead_company_id && (
          <ContactFormModal
            contact={editingContact}
            companyId={opp.lead_company_id}
            onClose={() => setEditingContact(null)}
            onSaved={() => { refetchContacts(); setEditingContact(null) }}
          />
        )}
        {showNewContactForm && opp.lead_company_id && (
          <ContactFormModal
            contact={null}
            companyId={opp.lead_company_id}
            onClose={() => setShowNewContactForm(false)}
            onSaved={() => { refetchContacts(); setShowNewContactForm(false) }}
          />
        )}
      </div>

      {/* ===== TASKS ===== */}
      <div className="px-6 py-4 border-b">
        <TasksPanel opportunityId={opp.id} />
      </div>

      {/* ===== DOCUMENTOS ANEXADOS ===== */}
      <div className="px-6 py-4 border-b">
        <AttachmentsPanel opportunityId={opp.id} />
      </div>

      {/* ===== REGISTROS DE INTERAÇÃO COM O CLIENTE ===== */}
      <div className="px-6 py-4">
        <h2 className="text-lg font-semibold mb-4">Registros de Interação com o Cliente</h2>

        {/* New Activity Form */}
        <div className="rounded-lg border bg-card p-4 mb-6">
          <CompactRichTextEditor
            value={content}
            onChange={setContent}
            placeholder="Descreva a interação com o cliente..."
            minHeight={80}
            onSubmit={handleSubmit}
          />
          <div className="flex items-center justify-between mt-3">
            <div className="flex gap-1">
              {(Object.keys(CHANNEL_CONFIG) as ActivityChannel[]).map((ch) => (
                <button
                  key={ch}
                  type="button"
                  onClick={() => setChannel(ch)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    channel === ch
                      ? `${CHANNEL_CONFIG[ch].bg} text-white`
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {CHANNEL_ICONS[ch]}
                  {CHANNEL_CONFIG[ch].label}
                </button>
              ))}
            </div>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={isActivityContentEmpty(content) || createActivity.isPending}
              isLoading={createActivity.isPending}
            >
              <Send className="h-4 w-4 mr-1" />Registrar
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Ctrl+Enter para enviar • B/I/U para formatar • Cole links para virarem clicáveis</p>
        </div>

        {/* Activities List */}
        {loadingActivities ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : !activities || activities.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">Nenhuma atividade registrada</p>
            <p className="text-sm mt-1">Adicione a primeira interacao acima</p>
          </div>
        ) : (
          <div className="space-y-6">
            {grouped.map((group) => (
              <div key={group.label}>
                {/* Date separator */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs font-medium text-muted-foreground px-2">{group.label}</span>
                  <div className="h-px flex-1 bg-border" />
                </div>

                <div className="space-y-3">
                  {group.items.map((act) => {
                    const chConfig = CHANNEL_CONFIG[act.channel] || CHANNEL_CONFIG.note
                    const isOwn = currentUser?.id === act.user_id
                    const isEditingThis = editingActivityId === act.id
                    const wasEdited = act.updated_at && act.updated_at !== act.created_at
                    return (
                      <div key={act.id} className="flex gap-3">
                        <UserAvatar name={act.user_name || 'N/A'} size="sm" className="mt-1 shrink-0" />
                        <div className="flex-1 rounded-lg border bg-card p-3 shadow-sm group" style={{ borderLeftWidth: '3px', borderLeftColor: chConfig.bg.replace('bg-', '').includes('muted') ? '#888' : undefined }}>
                          {/* Message header */}
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm">{act.user_name || 'Usuario'}</span>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${chConfig.bg} text-white`}>
                                {CHANNEL_ICONS[act.channel]}
                                {chConfig.label}
                              </span>
                              {wasEdited && <span className="text-[10px] text-muted-foreground italic" title={`Editado em ${formatTime(act.updated_at!)}`}>(editado)</span>}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-muted-foreground">{formatTime(act.created_at)}</span>
                              {isOwn && !isEditingThis && (
                                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button onClick={() => { setEditingActivityId(act.id); setEditingActivityContent(act.content) }} className="p-1 rounded hover:bg-muted" title="Editar"><Pencil className="h-3 w-3 text-muted-foreground" /></button>
                                  <button onClick={async () => { if (confirm('Excluir esta interação?')) { await deleteActivityMut.mutateAsync({ oppId: opp.id, activityId: act.id }) } }} className="p-1 rounded hover:bg-muted" title="Excluir"><Trash2 className="h-3 w-3 text-destructive" /></button>
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Message content */}
                          {isEditingThis ? (
                            <div className="space-y-2">
                              <CompactRichTextEditor
                                value={editingActivityContent}
                                onChange={setEditingActivityContent}
                                placeholder="Editar interação..."
                                minHeight={60}
                                autoFocus
                              />
                              <div className="flex justify-end gap-2">
                                <Button variant="ghost" size="sm" onClick={() => { setEditingActivityId(null); setEditingActivityContent('') }} disabled={updateActivityMut.isPending}>Cancelar</Button>
                                <Button size="sm" onClick={async () => {
                                  await updateActivityMut.mutateAsync({ oppId: opp.id, activityId: act.id, content: editingActivityContent })
                                  setEditingActivityId(null)
                                  setEditingActivityContent('')
                                }} disabled={updateActivityMut.isPending || isActivityContentEmpty(editingActivityContent)}>
                                  {updateActivityMut.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
                                  Salvar
                                </Button>
                              </div>
                            </div>
                          ) : looksLikeHtml(act.content) ? (
                            <div
                              className="prose prose-sm dark:prose-invert max-w-none text-sm [&_a]:text-primary [&_a]:underline [&_p]:my-2 [&_mark]:bg-yellow-200 dark:[&_mark]:bg-yellow-700/40 dark:[&_mark]:text-inherit"
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(act.content) }}
                            />
                          ) : (
                            <p className="text-sm whitespace-pre-wrap">{act.content}</p>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== RDC ===== */}
      <RdcPanel opportunityId={opp.id} />

      {/* ===== TIMELINE DE ATIVIDADES (eventos de sistema) ===== */}
      <div className="px-6 py-4 border-t">
        <h2 className="text-lg font-semibold mb-4">Timeline de Atividades</h2>
        <div className="rounded-lg border bg-muted/20 p-4 space-y-2 text-sm">
          <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0" /><span className="text-muted-foreground">Criada em</span><span className="font-medium">{formatDateTime(opp.created_at)}</span></div>
          {opp.updated_at && opp.updated_at !== opp.created_at && (
            <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" /><span className="text-muted-foreground">Última atualização</span><span className="font-medium">{formatDateTime(opp.updated_at)}</span></div>
          )}
          {opp.won_at && (
            <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" /><span className="text-muted-foreground">Ganha em</span><span className="font-medium text-green-600 dark:text-green-400">{formatDateTime(opp.won_at)}</span></div>
          )}
          {opp.lost_at && (
            <div className="flex items-center gap-2"><XCircle className="h-4 w-4 text-destructive flex-shrink-0" /><span className="text-muted-foreground">Perdida em</span><span className="font-medium text-destructive">{formatDateTime(opp.lost_at)}</span></div>
          )}
          {opp.proposals_count > 0 && (
            <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-amber-500 flex-shrink-0" /><span className="text-muted-foreground">Propostas vinculadas</span><span className="font-medium">{opp.proposals_count}</span></div>
          )}
        </div>
      </div>


      {/* ===== QUALIFY BANT MODAL (canônico) ===== */}
      {showQualifyModal && linkedLead && (
        <QualifyBantModal lead={linkedLead} onClose={() => setShowQualifyModal(false)} />
      )}

      {/* ===== EDIT OPPORTUNITY MODAL ===== */}
      {showCreateProposal && (
        <CreateProposalWizard
          opportunityId={opp.id}
          onClose={() => setShowCreateProposal(false)}
          onCreated={() => {
            proposals.refetch()
          }}
        />
      )}

      {editProposal && (
        <CreateProposalWizard
          opportunityId={opp.id}
          mode="edit"
          proposal={editProposal}
          onClose={() => { setEditProposal(null); proposals.refetch() }}
          onCreated={() => proposals.refetch()}
        />
      )}

      {deleteProposal && (
        <ConfirmDialog
          open={true}
          title="Excluir proposta?"
          message={`A proposta v${deleteProposal.version} sera permanentemente removida e desvinculada da oportunidade. Links publicos vinculados tambem serao apagados. Esta acao nao pode ser desfeita.`}
          confirmLabel="Excluir"
          variant="destructive"
          isLoading={deleteProposalMut.isPending}
          onConfirm={async () => {
            try {
              await deleteProposalMut.mutateAsync(deleteProposal.id)
              setDeleteProposal(null)
              proposals.refetch()
            } catch (e: any) {
              alert('Erro ao excluir: ' + (e?.response?.data?.detail || e?.message || ''))
            }
          }}
          onCancel={() => setDeleteProposal(null)}
        />
      )}

      {showNBA && (
        <NextBestActionModal oppId={opp.id} onClose={() => setShowNBA(false)} />
      )}
      {confirmDeleteOpp && (
        <ConfirmDialog
          open={true}
          title="Excluir oportunidade?"
          message={`"${opp.name}" será excluída permanentemente, junto com propostas, atividades e tarefas vinculadas. Esta ação não pode ser desfeita.`}
          confirmLabel="Excluir permanentemente"
          variant="destructive"
          isLoading={deletingOpp || deleteOppMut.isPending}
          onConfirm={handleDeleteOpp}
          onCancel={() => setConfirmDeleteOpp(false)}
        />
      )}
      {showEditOpp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowEditOpp(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative w-full max-w-md bg-card border rounded-xl shadow-2xl p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Pencil className="h-4 w-4 text-primary" /> Editar Oportunidade
              </h3>
              <button onClick={() => setShowEditOpp(false)}><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Nome *</label>
                <input
                  value={editForm.name}
                  onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md"
                  placeholder="Ex: IGA Petrobras"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Valor (R$) *</label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={editForm.value}
                    onChange={e => setEditForm({ ...editForm, value: parseFloat(e.target.value) || 0 })}
                    className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Probabilidade (%)</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={5}
                    value={editForm.probability}
                    onChange={e => setEditForm({ ...editForm, probability: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}
                    className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Previsão de fechamento</label>
                <input
                  type="date"
                  value={editForm.expected_close_date}
                  onChange={e => setEditForm({ ...editForm, expected_close_date: e.target.value })}
                  className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Status</label>
                <select
                  value={editForm.stage}
                  onChange={e => setEditForm({ ...editForm, stage: e.target.value as OpportunityStage })}
                  className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md"
                >
                  {STAGE_ORDER.map(s => <option key={s} value={s}>{STAGE_CONFIG[s].title}</option>)}
                </select>
              </div>
              <div className="text-xs text-muted-foreground bg-muted/30 rounded-md p-2.5 flex items-start gap-2">
                <FileText className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-amber-500" />
                <span>Para gerenciar propostas vinculadas, use a seção "Propostas" abaixo do cabeçalho.</span>
              </div>
              {editError && (
                <div className="text-xs text-destructive bg-destructive/10 rounded-md p-2.5 flex items-start gap-2">
                  <XCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span>{editError}</span>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-2 mt-5">
              <Button
                variant="ghost"
                className="text-destructive hover:bg-destructive/10"
                onClick={() => setConfirmDeleteOpp(true)}
                disabled={updateOppMut.isPending || moveStageMut.isPending || deletingOpp}
              >
                <Trash2 className="h-4 w-4 mr-1.5" /> Excluir
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowEditOpp(false)} disabled={updateOppMut.isPending || moveStageMut.isPending}>Cancelar</Button>
                <Button
                  onClick={saveEditOpp}
                  disabled={updateOppMut.isPending || moveStageMut.isPending || !editForm.name.trim() || editForm.value < 0}
                >
                  {(updateOppMut.isPending || moveStageMut.isPending) ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Salvando…</> : 'Salvar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}


// ===========================================================================
// AttachmentsPanel — upload + lista de documentos anexados à oportunidade
// ===========================================================================

const ATTACHMENT_ICON_MAP: Record<string, string> = {
  pdf: '📄', doc: '📝', docx: '📝',
  xls: '📊', xlsx: '📊', csv: '📊',
  ppt: '📽️', pptx: '📽️',
  png: '🖼️', jpg: '🖼️', jpeg: '🖼️', gif: '🖼️', webp: '🖼️', svg: '🖼️',
  zip: '🗜️', rar: '🗜️', '7z': '🗜️',
  txt: '📃',
}

function formatFileSize(bytes: number | null): string {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function isPreviewable(att: OpportunityAttachment): boolean {
  const t = (att.file_type || '').toLowerCase()
  return t === 'pdf' || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(t)
}

function AttachmentsPanel({ opportunityId }: { opportunityId: string }) {
  const { data: attachments, isLoading } = useOpportunityAttachments(opportunityId)
  const uploadMut = useUploadOpportunityAttachment()
  const deleteMut = useDeleteOpportunityAttachment()
  const [error, setError] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<OpportunityAttachment | null>(null)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setError('')
    try {
      await uploadMut.mutateAsync({ oppId: opportunityId, file })
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erro ao enviar arquivo')
    } finally {
      // Permite re-selecionar o mesmo arquivo se necessário
      e.target.value = ''
    }
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteMut.mutateAsync({ oppId: opportunityId, attId: confirmDelete.id })
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erro ao excluir anexo')
    }
    setConfirmDelete(null)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Paperclip className="h-5 w-5 text-muted-foreground" /> Documentos Anexados
          {attachments && attachments.length > 0 && (
            <span className="text-sm font-normal text-muted-foreground">({attachments.length})</span>
          )}
        </h2>
        <label className="cursor-pointer">
          <input
            type="file"
            className="hidden"
            onChange={handleFileSelect}
            disabled={uploadMut.isPending}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.gif,.webp,.svg,.zip,.rar,.7z"
          />
          <span className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50">
            {uploadMut.isPending ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Enviando…</>
            ) : (
              <><Upload className="h-4 w-4" /> Anexar Documento</>
            )}
          </span>
        </label>
      </div>

      {error && (
        <div className="mb-3 p-2.5 rounded bg-destructive/10 text-destructive text-sm flex items-start gap-2">
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-destructive/70 hover:text-destructive"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-muted-foreground py-4 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Carregando…</div>
      ) : !attachments || attachments.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center border-2 border-dashed rounded-lg">
          Nenhum documento anexado. Clique em <strong>Anexar Documento</strong> para enviar PDFs, planilhas, imagens etc.
        </div>
      ) : (
        <div className="space-y-2">
          {attachments.map((att) => {
            const ext = (att.file_type || '').toLowerCase()
            const icon = ATTACHMENT_ICON_MAP[ext] || '📎'
            return (
              <div key={att.id} className="border rounded-lg p-3 flex items-center gap-3 bg-card hover:bg-muted/30 transition-colors">
                <div className="text-2xl flex-shrink-0" aria-hidden>{icon}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate" title={att.file_name}>{att.file_name}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                    {att.file_size && <span>{formatFileSize(att.file_size)}</span>}
                    {att.file_type && <span className="uppercase font-mono">.{att.file_type}</span>}
                    {att.uploader_name && <span>· por {att.uploader_name}</span>}
                    <span>· {formatRelativeTime(att.created_at)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {isPreviewable(att) && (
                    <a
                      href={att.file_path}
                      target="_blank"
                      rel="noreferrer"
                      className="p-2 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                      title="Visualizar"
                    >
                      <Eye className="h-4 w-4" />
                    </a>
                  )}
                  <a
                    href={att.file_path}
                    download={att.file_name}
                    className="p-2 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                    title="Baixar"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                  <button
                    onClick={() => setConfirmDelete(att)}
                    className="p-2 rounded hover:bg-destructive/10 text-destructive"
                    title="Excluir"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4" onClick={() => setConfirmDelete(null)}>
          <div className="bg-card border rounded-xl shadow-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold mb-2">Excluir documento?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              "{confirmDelete.file_name}" será removido permanentemente. Não é possível desfazer.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
              <Button variant="destructive" size="sm" onClick={handleConfirmDelete} isLoading={deleteMut.isPending}>Excluir</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
